import { FilterValue } from '../types/index';
import { decodeData } from '../../../utils/encodeUtils';
import { decodeMappingStateFromUrl, MAPPING_URL_PARAM } from '../../../utils/mappingUrlState';
import Filters from '../Filters';

export const getMappingFiltersFromURL = (urlParams: URLSearchParams): FilterValue[] => {
  const encodedMapping = urlParams.get(MAPPING_URL_PARAM);
  const mappingFilters: FilterValue[] = [];
  if (!encodedMapping) return mappingFilters;

  const decodedMapping = decodeMappingStateFromUrl(encodedMapping);
  const currentLevel = decodedMapping?.stack?.[decodedMapping.currentIndex ?? 0];
  const mappingContent = currentLevel?.mappingContent as
    | { selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL'; selectedPmYear?: number }
    | undefined;

  if (mappingContent?.selectedReferenceType === 'PMID' || mappingContent?.selectedReferenceType === 'NCTID') {
    mappingFilters.push({
      name: 'reference_type',
      label: 'Reference type',
      operator: 'eq',
      type: 'select',
      value: [mappingContent.selectedReferenceType],
    } as FilterValue);
  }

  if (mappingContent?.selectedPmYear != null && Number.isFinite(Number(mappingContent.selectedPmYear))) {
    mappingFilters.push({
      name: 'pmYear',
      label: 'PM year',
      operator: 'eq',
      type: 'number',
      integer: true,
      value: String(Number(mappingContent.selectedPmYear)),
    } as FilterValue);
  }

  return mappingFilters;
};

export const initializeFiltersFromURL = (restDefaultFilterValue?: FilterValue[]): FilterValue[] => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedFilters = urlParams.get('f');

  let urlFilters: FilterValue[] = [];
  if (encodedFilters) {
    try {
      urlFilters = decodeData(encodedFilters);
    } catch {
      // Silent error handling - use empty array if decoding fails
    }
  }

  const baseFilters = Filters.filter((f: any) => f).map((f: any) => ({ ...f, value: f.value || '' }));
  const propFilters = Array.isArray(restDefaultFilterValue) ? restDefaultFilterValue : [];
  const mappingFilters = getMappingFiltersFromURL(urlParams);

  const mergeByName = (filters: FilterValue[]): FilterValue[] => {
    const merged = new Map<string, FilterValue>();
    filters.forEach(f => {
      if (f?.name) merged.set(f.name, { ...f, value: f.value || '' });
    });
    return Array.from(merged.values());
  };

  if (urlFilters.length > 0) {
    return mergeByName([...baseFilters, ...urlFilters, ...propFilters, ...mappingFilters]);
  }

  if (propFilters.length > 0 || mappingFilters.length > 0) {
    return mergeByName([...baseFilters, ...propFilters, ...mappingFilters]);
  }

  return baseFilters;
};

export const shouldShowRestrictionError = (
  restrictGridDisplay: boolean,
  restrictChemical: boolean,
  view: string,
  tab: string,
  isMapping?: boolean,
  mappingContent?: any,
): boolean => {
  return (
    (restrictGridDisplay && view === 'ALL') ||
    (restrictChemical && tab === 'CHEMICALS') ||
    (restrictChemical && view === 'CHEMICALS') ||
    (restrictChemical && isMapping && mappingContent?.tabSegment.includes('chem')) ||
    (restrictChemical && isMapping && mappingContent?.name === 'numberPmidsWithChemsIncludedInEvidence')
  );
};

// Re-export functions from SortFilterTables
export {
  getFiltersMap,
  getFilters,
  getSort,
  getMinFilterString,
  getMaxFilterString,
  getNumberFilter,
} from './SortFilterTables';

export function getSelectedRowsFromEnrichmentState<T = any>(
  rowData: T[],
  enrichmentRowSelection: Record<string, boolean>,
  getRowId: ((row: T, index: number) => string) | undefined,
): T[] {
  const getKey = getRowId ?? ((_: T, i: number) => String(i));
  return rowData.filter((row, index) => enrichmentRowSelection[getKey(row, index)]);
}

export function isRunEnrichmentEnabled(selectedCount: number): boolean {
  return selectedCount > 1;
}
